import { useEffect, useRef } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useCurrentUser } from '../../_shared/hooks';
import { useScanApi, useScansQuery } from '../hooks/useScanApi';
import { useLeadScannerStore } from '../store';
import { scansEligibleForSessionAutoEnrich } from '../enrich-clay';
import { computeEnrichmentUpdates } from '../scan-review-logic';
import { normalizeLinkedInInput } from '../linkedin-qr';

/**
 * Always-mounted at the app root (`index.tsx`, alongside
 * `GlobalGlazeToastRegion`) so the LinkedIn-enrichment auto-fire survives
 * in-app navigation — replacing `ScanReview.tsx`'s old per-mount
 * `autoEnrichQuery`, which died (no more observer, but the underlying fetch
 * itself kept running to completion regardless — react-query's cache still
 * received the result with zero observers) the moment the rep navigated away
 * from the scan that triggered it, e.g. to scan another badge. This
 * component never unmounts while the app is open, so it is always there to
 * receive that result and apply it.
 *
 * Reads the SAME `useScansQuery` the rest of the app already polls — no new
 * fetch — and scopes eligibility to scans created THIS SESSION
 * (`scansEligibleForSessionAutoEnrich`, `enrich-clay.ts`) rather than every
 * stale eligible draft in the list, so mounting the app never spends a real
 * Clay routine execution per old row as a surprise cost.
 *
 * Each eligible scan gets a `useQueries` entry with the EXACT SAME query key
 * shape (`['lead-scanner','enrich', scan.id]`), options, and `queryFn`
 * `ScanReview.tsx`'s manual "Enrich with Clay" button uses — sharing the key
 * is what dedupes a run already in flight (started by an earlier mount of
 * this manager, or by the old per-scan code before this change) rather than
 * double-firing it, and what lets `ScanReview.tsx`'s own `useIsFetching`
 * read (`isAutoEnriching`) pick this up with no extra wiring.
 */
export default function EnrichmentManager() {
  const { user } = useCurrentUser();
  const currentUserEmail = user?.ownerEmail ?? user?.email ?? null;
  const scanApi = useScanApi();
  const scansQuery = useScansQuery(currentUserEmail);
  const intakeBatchIds = useLeadScannerStore((s) => s.intakeBatchIds);

  const scans = scansQuery.data ?? [];
  const eligible = scansEligibleForSessionAutoEnrich(intakeBatchIds, scans);

  const results = useQueries({
    queries: eligible.map((scan) => ({
      queryKey: ['lead-scanner', 'enrich', scan.id],
      staleTime: Infinity,
      retry: false,
      meta: { suppressErrorToast: true },
      queryFn: () => scanApi.enrichLinkedIn(normalizeLinkedInInput(scan.linkedin)),
    })),
  });

  // Applied-once guard, keyed by scan id. Also naturally idempotent WITHOUT
  // this guard: once the PATCH below lands, the scan's fields are non-blank,
  // so `shouldAutoEnrich` (and therefore `scansEligibleForSessionAutoEnrich`)
  // flips false and the query stops being eligible on the next render — this
  // ref just avoids a redundant PATCH while that invalidation round trip is
  // still in flight.
  const appliedScanIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    eligible.forEach((scan, index) => {
      const data = results[index]?.data;
      if (!data || appliedScanIds.current.has(scan.id)) return;
      appliedScanIds.current.add(scan.id);

      const { updates } = computeEnrichmentUpdates(data, scan, 'auto');
      if (Object.keys(updates).length === 0) return;

      // Best-effort — a failed background persist must not throw out of this
      // effect; the rep still has the scan open (or will reopen it) with the
      // form's own fields to fall back on, same stance as
      // `ScanReview.tsx`'s `applyEnrichment`.
      void scanApi.patchScan(scan.id, updates).catch((err) => {
        console.error('[lead-scanner] EnrichmentManager failed to persist enrichment fields', err);
      });
    });
    // `results` (the `useQueries` array, unstable identity every render) is
    // the intentional re-run trigger; `eligible`/`scanApi` are derived from
    // the same render and read fresh via closure. oxlint's
    // `react/exhaustive-deps` flags this; the site is baselined in
    // oxlint-suppressions.json.
  }, [results]);

  return null;
}
